import React, { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import {
  ScrollText,
  Search,
  RefreshCw,
  RotateCcw,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  Ban,
  Hourglass,
  ChevronLeft,
  ChevronRight,
  Gamepad2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePermissions, PERMISSOES } from "../../../../hooks/usePermissions";
import {
  WIRED_STATUS,
  WiredResumo,
  WiredSincronizacao,
  WiredStatus,
  wiredService,
} from "../../../../services/wired";

const PAGE_SIZE = 20;
const AUTO_REFRESH_MS = 10000;
const TODOS = "all";

const STATUS_INFO: Record<WiredStatus, { label: string; badge: string; icon: React.ElementType }> = {
  pendente: {
    label: "Pendente",
    badge: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700",
    icon: Hourglass,
  },
  processando: {
    label: "Processando",
    badge: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700",
    icon: Loader2,
  },
  enviado: {
    label: "Enviado",
    badge: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700",
    icon: CheckCircle,
  },
  erro: {
    label: "Erro",
    badge: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700",
    icon: XCircle,
  },
  cancelado: {
    label: "Cancelado",
    badge: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600",
    icon: Ban,
  },
};

const FUNCIONALIDADES_PADRAO = [
  { codigo: "atualizar_patente", descricao: "Atualizar patente" },
  { codigo: "atualizar_sigla", descricao: "Atualizar sigla" },
  { codigo: "atualizar_status", descricao: "Atualizar status" },
  { codigo: "verificar_militar", descricao: "Verificar militar" },
];

const resumoVazio: WiredResumo = { pendente: 0, processando: 0, enviado: 0, erro: 0, cancelado: 0 };

function formatarData(data?: string | null) {
  if (!data) return "-";
  const parsed = new Date(data);
  if (isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("pt-BR");
}

function StatusBadge({ status }: { status: WiredStatus }) {
  const info = STATUS_INFO[status] ?? STATUS_INFO.cancelado;
  const Icon = info.icon;
  return (
    <Badge className={info.badge}>
      <Icon className={`h-3 w-3 mr-1 ${status === "processando" ? "animate-spin" : ""}`} />
      {info.label}
    </Badge>
  );
}

export function WiredLogs() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const podeReenviar = hasPermission(PERMISSOES.GERENCIAR_WIRED_CONFIGURACOES);

  const [items, setItems] = useState<WiredSincronizacao[]>([]);
  const [resumo, setResumo] = useState<WiredResumo>(resumoVazio);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<string>(TODOS);
  const [codigo, setCodigo] = useState<string>(TODOS);
  const [nickInput, setNickInput] = useState("");
  const [nick, setNick] = useState("");

  const [funcionalidades, setFuncionalidades] = useState(FUNCIONALIDADES_PADRAO);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [reenviandoId, setReenviandoId] = useState<string | null>(null);
  const [expandedErro, setExpandedErro] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const carregar = useCallback(
    async (silencioso = false) => {
      try {
        if (!silencioso) setLoading(true);
        const response = await wiredService.listarSincronizacoes({
          status: status === TODOS ? undefined : status,
          codigo: codigo === TODOS ? undefined : codigo,
          nick: nick || undefined,
          page,
          size: PAGE_SIZE,
        });
        setItems(response.items);
        setTotal(response.total);
        setResumo({ ...resumoVazio, ...response.resumo });
      } catch (error: any) {
        if (!silencioso) {
          enqueueSnackbar(error.response?.data?.message || "Erro ao carregar logs do Wired", {
            variant: "error",
          });
        }
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [status, codigo, nick, page]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => carregar(true), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, carregar]);

  // A lista de funcionalidades só é acessível a quem gerencia as configurações
  useEffect(() => {
    if (!podeReenviar) return;
    wiredService
      .listarFuncionalidades()
      .then((lista) => {
        if (lista.length > 0) {
          setFuncionalidades(lista.map((f) => ({ codigo: f.codigo, descricao: f.descricao })));
        }
      })
      .catch(() => undefined);
  }, [podeReenviar]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setNick(nickInput.trim());
  };

  const handleStatusChange = (value: string) => {
    setPage(1);
    setStatus(value);
  };

  const handleCardClick = (value: WiredStatus) => {
    handleStatusChange(status === value ? TODOS : value);
  };

  const handleLimparFiltros = () => {
    setPage(1);
    setStatus(TODOS);
    setCodigo(TODOS);
    setNickInput("");
    setNick("");
  };

  const handleReenviar = async (item: WiredSincronizacao) => {
    if (!window.confirm(`Deseja reenviar a sincronização de "${item.nick}"?`)) return;

    try {
      setReenviandoId(item.id);
      await wiredService.reenviarSincronizacao(item.id);
      enqueueSnackbar("Sincronização reenviada para a fila!", { variant: "success" });
      carregar(true);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao reenviar sincronização", {
        variant: "error",
      });
    } finally {
      setReenviandoId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
            Logs do Wired
          </h1>
          <p className="text-muted-foreground mt-2">
            Acompanhe a fila de sincronização de patentes e siglas com o Habbo
          </p>
        </div>
        {podeReenviar && (
          <Button
            variant="outline"
            onClick={() => navigate("/configuracoes/wired")}
            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
          >
            <Gamepad2 className="w-4 h-4 mr-2" />
            Configurações
          </Button>
        )}
      </div>

      {/* Resumo por status */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {WIRED_STATUS.map((s) => {
          const info = STATUS_INFO[s];
          const Icon = info.icon;
          return (
            <Card
              key={s}
              onClick={() => handleCardClick(s)}
              className={`cursor-pointer transition-colors hover:bg-muted ${
                status === s ? "ring-2 ring-[oklch(0.45_0.15_142)]" : ""
              }`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{info.label}</p>
                  <p className="text-2xl font-bold text-foreground">{resumo[s] ?? 0}</p>
                </div>
                <Badge className={info.badge}>
                  <Icon className="h-4 w-4" />
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros */}
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-foreground">
            <Search className="w-5 h-5" />
            Filtros
          </CardTitle>
          <CardDescription>Filtre a fila por status, funcionalidade ou nick</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={status} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos</SelectItem>
                  {WIRED_STATUS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_INFO[s].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Funcionalidade</Label>
              <Select
                value={codigo}
                onValueChange={(value) => {
                  setPage(1);
                  setCodigo(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todas</SelectItem>
                  {funcionalidades.map((f) => (
                    <SelectItem key={f.codigo} value={f.codigo}>
                      {f.descricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="nick">Nick</Label>
              <Input
                id="nick"
                value={nickInput}
                onChange={(e) => setNickInput(e.target.value)}
                placeholder="Digite o nick..."
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={loading}
                className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                Buscar
              </Button>
              <Button type="button" variant="outline" onClick={handleLimparFiltros} disabled={loading}>
                Limpar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Fila */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground">Fila de sincronização</CardTitle>
            <CardDescription>{total} registro(s) encontrado(s)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh((prev) => !prev)}
              title="Atualizar automaticamente a cada 10 segundos"
              className={autoRefresh ? "bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white" : ""}
            >
              <Clock className="w-4 h-4 mr-2" />
              Auto-atualizar {autoRefresh ? "ligado" : "desligado"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => carregar()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Carregando...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhum registro encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium whitespace-nowrap">Data</th>
                    <th className="py-2 pr-4 font-medium">Funcionalidade</th>
                    <th className="py-2 pr-4 font-medium">Nick</th>
                    <th className="py-2 pr-4 font-medium">Valor</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Tentativas</th>
                    <th className="py-2 pr-4 font-medium whitespace-nowrap">Próxima tentativa</th>
                    <th className="py-2 pr-4 font-medium whitespace-nowrap">Enviado em</th>
                    <th className="py-2 pr-4 font-medium">Responsável</th>
                    <th className="py-2 pr-4 font-medium">Último erro</th>
                    {podeReenviar && <th className="py-2 font-medium text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const erroLongo = !!item.ultimo_erro && item.ultimo_erro.length > 60;
                    const expandido = expandedErro === item.id;
                    return (
                      <tr key={item.id} className="border-b border-border hover:bg-muted transition-colors align-top">
                        <td className="py-3 pr-4 whitespace-nowrap">{formatarData(item.criado_em)}</td>
                        <td className="py-3 pr-4">
                          <div className="font-medium text-foreground">{item.funcionalidade_descricao}</div>
                          <div className="text-xs text-muted-foreground font-mono">{item.funcionalidade}</div>
                        </td>
                        <td className="py-3 pr-4 font-medium text-foreground">{item.nick}</td>
                        <td className="py-3 pr-4 font-mono">{item.valor ?? "-"}</td>
                        <td className="py-3 pr-4">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="py-3 pr-4">{item.tentativas}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          {item.status === "pendente" ? formatarData(item.proxima_tentativa_em) : "-"}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">{formatarData(item.enviado_em)}</td>
                        <td className="py-3 pr-4">{item.responsavel_nick || "-"}</td>
                        <td className="py-3 pr-4 max-w-xs">
                          {item.ultimo_erro ? (
                            <div>
                              <p
                                title={item.ultimo_erro}
                                className={`text-red-600 dark:text-red-400 break-words ${
                                  expandido ? "" : "line-clamp-2"
                                }`}
                              >
                                {item.ultimo_erro}
                              </p>
                              {erroLongo && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedErro(expandido ? null : item.id)}
                                  className="text-xs text-[oklch(0.45_0.15_142)] hover:underline"
                                >
                                  {expandido ? "Ver menos" : "Ver mais"}
                                </button>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        {podeReenviar && (
                          <td className="py-3 text-right">
                            {item.status === "erro" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReenviar(item)}
                                disabled={reenviandoId === item.id}
                                className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                              >
                                {reenviandoId === item.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                )}
                                Reenviar
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Próxima
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
